import type { IPoint } from './DrawUtils'

/**
 * B样条曲线计算工具类
 * 提供拟合点到控制点的转换、节点向量生成等功能
 */
export class SplineUtils {
  /**
   * 计算样条曲线的建议采样数，统一绘制预览与对象渲染阶段的采样规则。
   *
   * @param basePointCount - 基础点数量，通常为拟合点或控制点数
   * @param options - 可选采样配置；multiplier 默认 20，min 默认 50，max 默认 1000
   * @returns 采样点数量，范围 [min, max]
   */
  static getSampleCount(
    basePointCount: number,
    options?: {
      multiplier?: number
      min?: number
      max?: number
    }
  ): number {
    const multiplier = options?.multiplier ?? 20
    const min = options?.min ?? 50
    const max = options?.max ?? 1000
    return Math.max(min, Math.min(max, basePointCount * multiplier))
  }

  /**
   * 从拟合点计算 B 样条的控制点和节点向量
   * 开放曲线使用弦长参数三次插值，确保曲线通过所有拟合点并保持二阶连续
   *
   * @param fitPoints 拟合点数组
   * @param degree 阶数（固定为3）
   * @param startTangent 起点切线方向（可选）
   * @param endTangent 终点切线方向（可选）
   * @param isClosed 是否闭合
   * @returns 控制点数组和节点向量
   */
  static fitPointsToSpline(
    fitPoints: IPoint[],
    degree: number = 3,
    startTangent?: IPoint,
    endTangent?: IPoint,
    isClosed: boolean = false
  ): { controlPoints: IPoint[]; knots: number[]; degree: number } {
    const n = fitPoints.length

    if (n < 2) {
      throw new Error('至少需要2个拟合点')
    }

    // 对于 CAD 样条，使用 3 次 B 样条
    degree = 3

    if (n === 2) {
      // 两个点：直接使用线性插值
      return {
        controlPoints: [...fitPoints],
        knots: [0, 0, 1, 1],
        degree: 1,
      }
    }

    if (!isClosed) {
      return SplineUtils.fitOpenPointsToSpline(fitPoints, startTangent, endTangent)
    }

    // 闭合曲线沿用周期 Catmull-Rom 拟合
    const controlPoints = SplineUtils.catmullRomToControlPoints(
      fitPoints,
      startTangent,
      endTangent,
      isClosed
    )

    // 生成开放均匀节点向量
    const knots = SplineUtils.generateKnots(controlPoints.length, degree, isClosed)

    return { controlPoints, knots, degree }
  }

  /**
   * 使用弦长参数构造开放三次插值曲线，并转换为逐段三次 Bezier 的 NURBS 表示。
   */
  private static fitOpenPointsToSpline(
    fitPoints: IPoint[],
    startTangent?: IPoint,
    endTangent?: IPoint
  ): { controlPoints: IPoint[]; knots: number[]; degree: number } {
    const parameters = SplineUtils.getChordLengthParameters(fitPoints)
    const secondDerivatives = SplineUtils.solveSecondDerivatives(
      fitPoints,
      parameters,
      startTangent,
      endTangent
    )
    const controlPoints: IPoint[] = [{ ...fitPoints[0] }]

    for (let i = 0; i < fitPoints.length - 1; i++) {
      const p0 = fitPoints[i]
      const p1 = fitPoints[i + 1]
      const m0 = secondDerivatives[i]
      const m1 = secondDerivatives[i + 1]
      const interval = parameters[i + 1] - parameters[i]
      const slope = {
        x: (p1.x - p0.x) / interval,
        y: (p1.y - p0.y) / interval,
      }
      const startDerivative = {
        x: slope.x - (interval * (2 * m0.x + m1.x)) / 6,
        y: slope.y - (interval * (2 * m0.y + m1.y)) / 6,
      }
      const endDerivative = {
        x: slope.x + (interval * (m0.x + 2 * m1.x)) / 6,
        y: slope.y + (interval * (m0.y + 2 * m1.y)) / 6,
      }

      controlPoints.push(
        {
          x: p0.x + (startDerivative.x * interval) / 3,
          y: p0.y + (startDerivative.y * interval) / 3,
        },
        {
          x: p1.x - (endDerivative.x * interval) / 3,
          y: p1.y - (endDerivative.y * interval) / 3,
        },
        { ...p1 }
      )
    }

    const knots = [0, 0, 0, 0]
    for (let i = 1; i < parameters.length - 1; i++) {
      knots.push(parameters[i], parameters[i], parameters[i])
    }
    knots.push(1, 1, 1, 1)

    return { controlPoints, knots, degree: 3 }
  }

  /** 计算归一化弦长参数；重合点使用极小正间隔，避免退化节点。 */
  private static getChordLengthParameters(points: IPoint[]): number[] {
    const distances: number[] = []
    let total = 0
    for (let i = 1; i < points.length; i++) {
      const distance = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y)
      const safeDistance = Math.max(distance, 1e-9)
      distances.push(safeDistance)
      total += safeDistance
    }

    const parameters = [0]
    let accumulated = 0
    for (const distance of distances) {
      accumulated += distance
      parameters.push(accumulated / total)
    }
    parameters[parameters.length - 1] = 1
    return parameters
  }

  /** 求解自然边界或指定端点切线边界下的三次样条二阶导数。 */
  private static solveSecondDerivatives(
    points: IPoint[],
    parameters: number[],
    startTangent?: IPoint,
    endTangent?: IPoint
  ): IPoint[] {
    const count = points.length
    const lower = new Array<number>(count).fill(0)
    const diagonal = new Array<number>(count).fill(0)
    const upper = new Array<number>(count).fill(0)
    const rightX = new Array<number>(count).fill(0)
    const rightY = new Array<number>(count).fill(0)
    const firstInterval = parameters[1] - parameters[0]
    const lastInterval = parameters[count - 1] - parameters[count - 2]

    if (startTangent) {
      diagonal[0] = 2 * firstInterval
      upper[0] = firstInterval
      rightX[0] = 6 * ((points[1].x - points[0].x) / firstInterval - startTangent.x)
      rightY[0] = 6 * ((points[1].y - points[0].y) / firstInterval - startTangent.y)
    } else {
      diagonal[0] = 1
    }

    for (let i = 1; i < count - 1; i++) {
      const previousInterval = parameters[i] - parameters[i - 1]
      const nextInterval = parameters[i + 1] - parameters[i]
      lower[i] = previousInterval
      diagonal[i] = 2 * (previousInterval + nextInterval)
      upper[i] = nextInterval
      rightX[i] =
        6 *
        ((points[i + 1].x - points[i].x) / nextInterval -
          (points[i].x - points[i - 1].x) / previousInterval)
      rightY[i] =
        6 *
        ((points[i + 1].y - points[i].y) / nextInterval -
          (points[i].y - points[i - 1].y) / previousInterval)
    }

    if (endTangent) {
      lower[count - 1] = lastInterval
      diagonal[count - 1] = 2 * lastInterval
      rightX[count - 1] =
        6 * (endTangent.x - (points[count - 1].x - points[count - 2].x) / lastInterval)
      rightY[count - 1] =
        6 * (endTangent.y - (points[count - 1].y - points[count - 2].y) / lastInterval)
    } else {
      diagonal[count - 1] = 1
    }

    const solvedX = SplineUtils.solveTridiagonal(lower, diagonal, upper, rightX)
    const solvedY = SplineUtils.solveTridiagonal(lower, diagonal, upper, rightY)
    return solvedX.map((x, index) => ({ x, y: solvedY[index] }))
  }

  /** Thomas 算法求解三对角线性方程组。 */
  private static solveTridiagonal(
    lower: number[],
    diagonal: number[],
    upper: number[],
    right: number[]
  ): number[] {
    const modifiedDiagonal = [...diagonal]
    const modifiedUpper = [...upper]
    const modifiedRight = [...right]

    for (let i = 1; i < modifiedDiagonal.length; i++) {
      const factor = lower[i] / modifiedDiagonal[i - 1]
      modifiedDiagonal[i] -= factor * modifiedUpper[i - 1]
      modifiedRight[i] -= factor * modifiedRight[i - 1]
    }

    const result = new Array<number>(modifiedDiagonal.length)
    result[result.length - 1] =
      modifiedRight[result.length - 1] / modifiedDiagonal[result.length - 1]
    for (let i = result.length - 2; i >= 0; i--) {
      result[i] = (modifiedRight[i] - modifiedUpper[i] * result[i + 1]) / modifiedDiagonal[i]
    }
    return result
  }

  /**
   * 使用 Catmull-Rom 插值计算 B 样条控制点
   * 确保曲线通过所有拟合点
   */
  private static catmullRomToControlPoints(
    fitPoints: IPoint[],
    startTangent?: IPoint,
    endTangent?: IPoint,
    isClosed: boolean = false
  ): IPoint[] {
    const n = fitPoints.length
    const controlPoints: IPoint[] = []

    // 计算每个拟合点的切线
    const tangents: IPoint[] = []

    for (let i = 0; i < n; i++) {
      let tangent: IPoint

      if (i === 0) {
        // 起点切线
        if (startTangent) {
          tangent = startTangent
        } else if (isClosed) {
          tangent = {
            x: (fitPoints[1].x - fitPoints[n - 1].x) * 0.5,
            y: (fitPoints[1].y - fitPoints[n - 1].y) * 0.5,
          }
        } else {
          tangent = {
            x: fitPoints[1].x - fitPoints[0].x,
            y: fitPoints[1].y - fitPoints[0].y,
          }
        }
      } else if (i === n - 1) {
        // 终点切线
        if (endTangent) {
          tangent = endTangent
        } else if (isClosed) {
          tangent = {
            x: (fitPoints[0].x - fitPoints[n - 2].x) * 0.5,
            y: (fitPoints[0].y - fitPoints[n - 2].y) * 0.5,
          }
        } else {
          tangent = {
            x: fitPoints[n - 1].x - fitPoints[n - 2].x,
            y: fitPoints[n - 1].y - fitPoints[n - 2].y,
          }
        }
      } else {
        // 中间点：Catmull-Rom 切线（中心差分）
        tangent = {
          x: (fitPoints[i + 1].x - fitPoints[i - 1].x) * 0.5,
          y: (fitPoints[i + 1].y - fitPoints[i - 1].y) * 0.5,
        }
      }

      tangents.push(tangent)
    }

    // 从拟合点和切线生成控制点
    // 对于 3 次 Hermite/Catmull-Rom 样条，每个段需要 4 个控制点
    // P0, P1, P2, P3，其中曲线通过 P0 和 P3

    // 第一个控制点就是第一个拟合点
    controlPoints.push({ ...fitPoints[0] })

    for (let i = 0; i < n - 1; i++) {
      const p0 = fitPoints[i]
      const p1 = fitPoints[i + 1]
      const t0 = tangents[i]
      const t1 = tangents[i + 1]

      // Hermite 插值：从切线计算控制点
      // C1 = P0 + T0/3
      const c1 = {
        x: p0.x + t0.x / 3,
        y: p0.y + t0.y / 3,
      }

      // C2 = P1 - T1/3
      const c2 = {
        x: p1.x - t1.x / 3,
        y: p1.y - t1.y / 3,
      }

      // 添加两个中间控制点
      if (i === 0 || !SplineUtils.pointsEqual(controlPoints[controlPoints.length - 1], c1)) {
        controlPoints.push(c1)
      }
      controlPoints.push(c2)

      // 添加下一个拟合点（除了最后一个段）
      if (i < n - 2) {
        controlPoints.push({ ...p1 })
      }
    }

    // 最后一个控制点
    controlPoints.push({ ...fitPoints[n - 1] })

    return controlPoints
  }

  /**
   * 判断两个点是否相等
   */
  private static pointsEqual(p1: IPoint, p2: IPoint, epsilon: number = 1e-10): boolean {
    return Math.abs(p1.x - p2.x) < epsilon && Math.abs(p1.y - p2.y) < epsilon
  }

  /**
   * 生成通用节点向量，供控制点创建 B 样条使用。
   *
   * @param numControlPoints - 控制点数量
   * @param degree - 样条阶数
   * @param isClosed - 是否闭合，闭合时生成周期节点，默认 false
   * @returns 节点向量，长度 numControlPoints + degree + 1
   */
  static generateKnots(
    numControlPoints: number,
    degree: number,
    isClosed: boolean = false
  ): number[] {
    const knots: number[] = []
    const n = numControlPoints - 1
    const m = n + degree + 1

    if (isClosed) {
      // 周期性
      for (let i = 0; i <= m; i++) {
        knots.push(i / (m - degree))
      }
    } else {
      // 开放均匀
      for (let i = 0; i <= degree; i++) {
        knots.push(0)
      }

      const interior = n - degree + 1
      for (let i = 1; i < interior; i++) {
        knots.push(i / interior)
      }

      for (let i = 0; i <= degree; i++) {
        knots.push(1)
      }
    }

    return knots
  }

  /**
   * 从控制点反推拟合点的近似实现，在曲线上均匀采样。
   *
   * @param controlPoints - 控制点列表
   * @param knots - 节点向量
   * @param degree - 样条阶数
   * @param numSamples - 采样段数，默认 20，生成 numSamples + 1 个点
   * @returns 拟合点近似列表
   */
  static approximateFitPoints(
    controlPoints: IPoint[],
    knots: number[],
    degree: number,
    numSamples: number = 20
  ): IPoint[] {
    const fitPoints: IPoint[] = []

    for (let i = 0; i <= numSamples; i++) {
      const u = i / numSamples
      const point = SplineUtils.evaluateBSpline(u, controlPoints, knots, degree)
      fitPoints.push(point)
    }

    return fitPoints
  }

  /**
   * 计算 B 样条曲线在参数 u 处的点。
   *
   * @param u - 参数值，通常位于 [0, 1]
   * @param controlPoints - 控制点列表
   * @param knots - 节点向量
   * @param degree - 样条阶数
   * @returns 曲线在 u 处的点
   */
  static evaluateBSpline(
    u: number,
    controlPoints: IPoint[],
    knots: number[],
    degree: number
  ): IPoint {
    const n = controlPoints.length
    let x = 0
    let y = 0

    for (let i = 0; i < n; i++) {
      const basis = SplineUtils.basisFunction(i, degree, u, knots)
      x += basis * controlPoints[i].x
      y += basis * controlPoints[i].y
    }

    return { x, y }
  }

  /**
   * Cox-de Boor 递归公式计算 B 样条基函数
   * N(i,p,u) 表示第 i 个控制点在 p 阶、参数 u 处的基函数值
   */
  private static basisFunction(i: number, p: number, u: number, knots: number[]): number {
    // 0 阶基函数
    if (p === 0) {
      return u >= knots[i] && u < knots[i + 1] ? 1 : 0
    }

    // 递归计算
    const left = knots[i + p] - knots[i]
    const right = knots[i + p + 1] - knots[i + 1]

    let result = 0

    if (left > 0) {
      result += ((u - knots[i]) / left) * SplineUtils.basisFunction(i, p - 1, u, knots)
    }

    if (right > 0) {
      result += ((knots[i + p + 1] - u) / right) * SplineUtils.basisFunction(i + 1, p - 1, u, knots)
    }

    return result
  }
}
